import { useMemo } from "react";
import type { GetAllCompanionChatsForUserQuery } from "../graphql/generated/graphql";
import { formatTimeAgo } from "../utils/date";
import { WorkspaceType } from "../components/chat-companion/WorkspaceCard/WorkspaceCard";
import type { WorkspaceSidebarWorkspace } from "../components/chat-companion/WorkspaceSidebar/WorkspaceSidebar";

export const GENERAL_WORKSPACE_ID = "general_chats";
export const TODO_WORKSPACE_ID = "todo";
export const SETTINGS_WORKSPACE_ID = "settings";

type CompanionWorkspaces = NonNullable<GetAllCompanionChatsForUserQuery["getAllCompanionChatsForUser"]>;

const TODO_WORKSPACES: WorkspaceSidebarWorkspace[] = [
  {
    id: TODO_WORKSPACE_ID,
    title: "To-Dos",
    type: "todo" as WorkspaceType,
  },
];

const SETTINGS_WORKSPACES: WorkspaceSidebarWorkspace[] = [
  {
    id: SETTINGS_WORKSPACE_ID,
    title: "Settings",
    type: "settings" as WorkspaceType,
  },
];

const getLastActivityLabel = (
  chats: Array<{ last_modified?: string | null } | null> | null | undefined,
  allowUndefined?: boolean
) => {
  const lastModified = chats?.[0]?.last_modified;
  if (!lastModified) {
    return allowUndefined ? undefined : "";
  }
  return formatTimeAgo(lastModified);
};

export function useWorkspaceLists({
  generalChats,
  courseWorkspaces,
  supportWorkspaces,
  activeWorkspaceId,
}: {
  generalChats: CompanionWorkspaces["generalChats"];
  courseWorkspaces: CompanionWorkspaces["courseWorkspaces"];
  supportWorkspaces: CompanionWorkspaces["supportWorkspaces"];
  activeWorkspaceId: string | null;
}) {
  const generalWorkspaces = useMemo(() => {
    return [
      {
        id: GENERAL_WORKSPACE_ID,
        title: "General Chat",
        type: "general" as WorkspaceType,
        threadCount: generalChats.length,
        lastActivityLabel: getLastActivityLabel(generalChats, true),
        isActive: activeWorkspaceId === GENERAL_WORKSPACE_ID,
      },
    ];
  }, [generalChats, activeWorkspaceId]);

  const courseWorkspaceItems = useMemo(() => {
    if (courseWorkspaces.length === 0) return [];

    return courseWorkspaces.map((workspace) => ({
      id: workspace.id,
      title: workspace.name,
      type: "course" as WorkspaceType,
      threadCount: workspace.chats?.length ?? 0,
      lastActivityLabel: getLastActivityLabel(workspace.chats),
      isActive: activeWorkspaceId === workspace.id,
    }));
  }, [courseWorkspaces, activeWorkspaceId]);

  const supportWorkspaceItems = useMemo(() => {
    if (supportWorkspaces.length === 0) return [];

    return supportWorkspaces.map((workspace) => ({
      id: workspace.id,
      title: workspace.name,
      type: "support" as WorkspaceType,
      threadCount: workspace.chats?.length ?? 0,
      lastActivityLabel: getLastActivityLabel(workspace.chats),
      isActive: activeWorkspaceId === workspace.id,
    }));
  }, [supportWorkspaces, activeWorkspaceId]);

  return {
    generalWorkspaces,
    courseWorkspaceItems,
    supportWorkspaceItems,
    todoWorkspaces: TODO_WORKSPACES,
    settingsWorkspaces: SETTINGS_WORKSPACES,
  };
}
